<template>
  <div>
    <PageTitle
      title="Purchases"
      :btnCreate="true"
      :createRoute="'/purchase/add'"
      :permission="'Purchase Create'"
    />
    <v-container fluid class="lighten-12 container">
      <v-card class="lighten-12 card-content">
        <TableFilters
          :filters="[
            'supplier',
            'search',
            'dateRange',
            'total',
            'warehouse',
            'purchaseStatus',
          ]"
          v-model="filter"
          :columns="columns"
        />
      </v-card>

      <v-card class="lighten-12 mt-2 list-table">
        <v-container fluid class="light gray lighten-12">
          <ValidationObserver ref="observer">
            <form>
              <!-- Messages -->
              <v-row v-if="messages.length">
                <v-col>
                  <v-card>
                    <v-card-text>
                      <v-row>
                        <v-alert
                          v-for="msg in messages"
                          :key="msg"
                          class="messages"
                          outlined
                          type="error"
                        >
                          {{ msg }}
                        </v-alert>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <v-row>
                <v-card-text class="pa-0">
                  <v-data-table
                    :items-per-page="paginationOptions.perPage"
                    :headers="headers"
                    :items="PurchaseList"
                    hide-default-footer
                  >
                    <!-- No Data -->
                    <template v-slot:no-data>
                      <div class="mt-16 container justify-center item-center">
                        <noData name="Purchase" />
                      </div>
                    </template>

                    <!-- Reference Number -->
                    <template v-slot:item.reference_number="{ item }">
                      <CopyTableCell :text="item.reference_number" />
                    </template>

                    <!-- Date -->
                    <template v-slot:item.date="{ item }">
                      {{ item.date | formatDate }}
                    </template>

                    <!-- Amounts -->
                    <template v-slot:item.paid_amount="{ item }">
                      <strong class="px-4">{{
                        item.paid_amount | formatCurrency
                      }}</strong>
                    </template>

                    <template v-slot:item.due="{ item }">
                      <strong class="px-4">{{
                        item.due | formatCurrency
                      }}</strong>
                    </template>

                    <template v-slot:item.total_amount="{ item }">
                      <strong class="px-4">{{
                        item.total_amount | formatCurrency
                      }}</strong>
                    </template>

                    <!-- Status -->
                    <template v-slot:item.status="{ item }">
                      <v-chip
                        :x-small="true"
                        label
                        text-color="white"
                        :color="GetChequeStatusColor(item.status)"
                        dark
                      >
                        {{ item.status }}
                      </v-chip>
                    </template>

                    <!-- Payment Status -->
                    <template v-slot:item.payment_status="{ item }">
                      <v-chip
                        :x-small="true"
                        label
                        text-color="white"
                        :color="
                          item.paid_amount &&
                          item.sub_total_amount <= item.paid_amount
                            ? 'green'
                            : '#FF4D4D'
                        "
                        dark
                      >
                        {{
                          getPaymentStatus(
                            item.sub_total_amount,
                            item.paid_amount
                          )
                        }}
                      </v-chip>
                    </template>

                    <!-- Actions -->
                    <template v-slot:item.actions="{ item }">
                      <list-menu
                        v-if="item.status == 'Pending'"
                        feature="purchase"
                        :item="item"
                        viewPermission="Purchase Show"
                        editPermission="Purchase Edit"
                        @refreshList="refreshData"
                      />
                      <list-menu
                        v-else
                        feature="purchase"
                        :item="item"
                        viewPermission="Purchase Show"
                        @refreshList="refreshData"
                      />
                    </template>

                    <!-- Footer Totals -->
                    <template
                      slot="body.append"
                      v-if="PurchaseList && PurchaseList.length"
                    >
                      <tr class="black--text">
                        <th class="title"></th>
                        <th class="title"></th>
                        <th class="title"></th>
                        <th class="title"></th>
                        <th class="title"></th>
                        <th>
                          <h3 class="justify-content-end pr-2">
                            {{ total | formatCurrency }}
                          </h3>
                        </th>
                        <th>
                          <h3 class="justify-content-end">
                            {{ paid | formatCurrency }}
                          </h3>
                        </th>
                        <th>
                          <h3 class="justify-content-end">
                            {{ due | formatCurrency }}
                          </h3>
                        </th>
                        <th class="title"></th>
                      </tr>
                    </template>

                    <!-- Pagination -->
                    <template v-slot:footer="{}">
                      <paginationComponent
                        @paginationOptions="setPaginationOptions"
                        :url="'purchases'"
                        @response="receivePurchaseData"
                        :filter="filter"
                        ref="pagination"
                      />
                    </template>
                  </v-data-table>
                </v-card-text>
              </v-row>
            </form>
          </ValidationObserver>
        </v-container>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import moment from "moment";
import { has } from "lodash";
import { extend, ValidationObserver, ValidationProvider } from "vee-validate";
import { numeric } from "vee-validate/dist/rules";
import TableFilters from "@/components/base/TableFilters";
import paginationComponent from "../../shared/components/pagination.vue";
import noData from "../../../components/shared/noItem";

extend("numeric", { ...numeric, message: "input must be numeric" });

export default {
  data: () => ({
    paginationOptions: {},
    messages: [],
    columns: [
      { text: "Date", value: "date", show: true, align: "left", width: "8%" },
      {
        text: "Reference No",
        value: "reference_number",
        show: true,
        align: "left",
        width: "12%",
      },
      {
        text: "Supplier",
        value: "supplier.name",
        show: true,
        align: "left",
        width: "9%",
      },
      {
        text: "Warehouse",
        value: "wareHouse.name",
        show: true,
        align: "left",
        width: "9%",
      },
      {
        text: "Purchase Status",
        value: "status",
        show: true,
        align: "center",
        width: "10%",
      },
      {
        text: "Total",
        value: "total_amount",
        show: true,
        align: "right",
        width: "9%",
      },
      {
        text: "Paid",
        value: "paid_amount",
        show: true,
        align: "right",
        width: "9%",
      },
      { text: "Due", value: "due", show: true, align: "right", width: "8%" },
      {
        text: "Payment Status",
        value: "payment_status",
        show: true,
        align: "center",
        width: "10%",
      },
      {
        text: "Actions",
        value: "actions",
        show: true,
        align: "center",
        width: "9%",
      },
    ],
    PurchaseList: [],
    filter: {
      invoice_status: "",
      start: "",
      end: "",
      amount: "",
      supplier: "",
      search: "",
    },
    menu: false,
    total: 0,
    paid: 0,
    due: 0,
  }),
  components: {
    ValidationProvider,
    ValidationObserver,
    paginationComponent,
    noData,
    TableFilters, 
  },
  computed: {
    headers() {
      return this.columns.filter((item) => item.show);
    },
  },
  methods: {
    getPaymentStatus(total, paid) {
      return total <= paid ? "Paid" : "Due";
    },
    setPaginationOptions(data) {
      this.paginationOptions = data;
    },
    refreshData() {
      this.$refs.pagination.refreshData();
    },
    receivePurchaseData(data) {
      this.PurchaseList = data;
      this.PurchaseList.forEach((el) => {
        el.due = el.sub_total_amount - el.paid_amount;
      });
      this.due = this.sumField(this.PurchaseList, "due");
      this.total = this.sumField(this.PurchaseList, "total_amount");
      this.paid = this.sumField(this.PurchaseList, "paid_amount");
    },
    sumField(array, field) {
      return array.reduce((sum, el) => sum + el[field], 0);
    },
    GetChequeStatusColor(status) {
      switch (status) {
        case "Completed":
          return "green";
        case "Pending":
          return "orange";
        case "Canceled":
          return "red";
        default:
          return "gray";
      }
    },
    getStatusColor(status) {
      return status ? "green" : "gray";
    },
    viewPurchaseOrder(order) {
      this.$router.push(`/purchases/${order.id}`);
    },
    editPurchaseOrder(order) {
      this.$router.push(`/purchases/edit/${order.id}`);
    },
  },
  filters: {
    hasName(value) {
      return has(value, "name") ? value.name : "-";
    },
    formatDate(value) {
      return value ? moment(value).format("DD/MM/YYYY") : "-";
    },
  },
};
</script>

<style>
.v-data-table > .v-data-table__wrapper > table > tbody > tr > td {
}
.theme--light.v-data-table
  > .v-data-table__wrapper
  > table
  > thead
  > tr:last-child
  > th {
}
.v-application--is-ltr
  .v-data-table
  > .v-data-table__wrapper
  > table
  > tbody
  > tr
  > th {
}
</style>
